import { useCallback, useEffect, useMemo, useState } from "react";
import { featuresApi } from "../api/featuresApi";

/**
 * Loads the Features master list + lazy-loaded sub-features per feature,
 * plus the Products meta list used by filters and the create/edit drawer.
 *
 * Filters: { q, product_id, asset, ip_type }
 *   - q         — free-text search applied client-side
 *   - product_id — passes through to GET /features?product_id=
 *   - asset      — filter features whose applicable_asset[] includes the value
 *   - ip_type    — sub-feature flag; matched when any expanded sub-feature
 *                  has this ip_type (kept as a hook-level filter so the UI can
 *                  show it in the toolbar even though it's a sub-feature
 *                  attribute)
 */
export function useFeaturesData() {
  const [features, setFeatures] = useState([]);
  const [products, setProducts] = useState([]);
  const [subFeatures, setSubFeatures] = useState({});      // { [featureId]: SubFeature[] }
  const [subLoading, setSubLoading] = useState({});        // { [featureId]: boolean }
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    q: "",
    product_id: "",
    asset: "",
  });

  const fetchFeatures = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.product_id) params.product_id = filters.product_id;
      const data = await featuresApi.listFeatures(params);
      setFeatures(Array.isArray(data) ? data : []);
    } catch {
      // keep prior list on error
    } finally {
      setLoading(false);
    }
  }, [filters.product_id]);

  const fetchMeta = useCallback(async () => {
    try {
      const data = await featuresApi.listProducts();
      setProducts(Array.isArray(data) ? data : []);
    } catch {
      // non-critical
    }
  }, []);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  useEffect(() => {
    fetchMeta();
  }, [fetchMeta]);

  // Lazy-load sub-features for one feature. Cached per featureId until refetch().
  const loadSubsFor = useCallback(async (featureId) => {
    if (!featureId) return;
    if (subFeatures[featureId] !== undefined) return;     // already cached
    setSubLoading((prev) => ({ ...prev, [featureId]: true }));
    try {
      const data = await featuresApi.listSubFeatures({ feature_id: featureId });
      setSubFeatures((prev) => ({ ...prev, [featureId]: Array.isArray(data) ? data : [] }));
    } catch {
      setSubFeatures((prev) => ({ ...prev, [featureId]: [] }));
    } finally {
      setSubLoading((prev) => ({ ...prev, [featureId]: false }));
    }
  }, [subFeatures]);

  const refetchSubsFor = useCallback(async (featureId) => {
    if (!featureId) return;
    setSubLoading((prev) => ({ ...prev, [featureId]: true }));
    try {
      const data = await featuresApi.listSubFeatures({ feature_id: featureId });
      setSubFeatures((prev) => ({ ...prev, [featureId]: Array.isArray(data) ? data : [] }));
    } catch {
      // keep prior on error
    } finally {
      setSubLoading((prev) => ({ ...prev, [featureId]: false }));
    }
  }, []);

  const filteredFeatures = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    const asset = filters.asset.trim().toLowerCase();
    return features.filter((f) => {
      if (asset) {
        const list = Array.isArray(f.applicable_asset) ? f.applicable_asset : [];
        if (!list.some((a) => String(a).toLowerCase() === asset)) return false;
      }
      if (!q) return true;
      const hay = [
        f.feature_code,
        f.feature_name,
        f.description,
        f.product_name,
        f.latest_version,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [features, filters.q, filters.asset]);

  return {
    features:    filteredFeatures,
    totalFeatures: features.length,
    products,
    subFeatures,
    subLoading,
    loading,
    filters,
    setFilters,
    loadSubsFor,
    refetchSubsFor,
    refetch: fetchFeatures,
    refetchProducts: fetchMeta,
  };
}
